import React, { useState, useEffect } from 'react';
import { t } from '../../i18n';
import { realtyApi } from './realtyApi';
import { isRealtyLoggedIn } from './realtyApi';

export function EMICalculator({ propertyPrice }) {
  const [show, setShow] = useState(false);
  const [downPaymentPercent, setDownPaymentPercent] = useState(20);
  const [rate, setRate] = useState(12);
  const [years, setYears] = useState(20);

  const principal = propertyPrice * (1 - downPaymentPercent / 100);
  const monthlyRate = rate / 100 / 12;
  const months = years * 12;
  const emi = monthlyRate > 0
    ? (principal * monthlyRate * Math.pow(1 + monthlyRate, months)) / (Math.pow(1 + monthlyRate, months) - 1)
    : principal / months;
  const totalPayment = emi * months;
  const totalInterest = totalPayment - principal;

  const inputStyle = { width: '100%', padding: '8px 10px', fontSize: 13, border: '1px solid var(--border)', borderRadius: 8, boxSizing: 'border-box' };
  const labelStyle = { fontSize: 12, color: 'var(--muted)', display: 'block', marginBottom: 4 };

  return (
    <div className="side-card">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }} onClick={() => setShow(!show)}>
        <h3 style={{ fontSize: 16, margin: 0 }}>💰 Mortgage / EMI Calculator</h3>
        <span style={{ fontSize: 12 }}>{show ? '▲' : '▼'}</span>
      </div>
      {show && (
        <div style={{ marginTop: 14 }}>
          <div style={{ display: 'grid', gap: 10, marginBottom: 12 }}>
            <div>
              <label style={labelStyle}>Down payment: {downPaymentPercent}%</label>
              <input type="range" min="0" max="90" value={downPaymentPercent} onChange={(e) => setDownPaymentPercent(Number(e.target.value))} style={{ width: '100%' }} />
            </div>
            <div>
              <label style={labelStyle}>Interest rate: {rate}% per year</label>
              <input type="range" min="1" max="25" step="0.5" value={rate} onChange={(e) => setRate(Number(e.target.value))} style={{ width: '100%' }} />
            </div>
            <div>
              <label style={labelStyle}>Loan tenure: {years} years</label>
              <input type="range" min="1" max="30" value={years} onChange={(e) => setYears(Number(e.target.value))} style={{ width: '100%' }} />
            </div>
          </div>
          <div style={{ background: 'var(--cream)', borderRadius: 8, padding: 12 }}>
            <p style={{ fontSize: 13, margin: '0 0 6px' }}>Loan amount: <b>Rs {principal.toLocaleString(undefined, { maximumFractionDigits: 0 })}</b></p>
            <p style={{ fontSize: 20, fontWeight: 700, color: 'var(--emerald-dark)', margin: '0 0 6px' }}>Rs {emi.toLocaleString(undefined, { maximumFractionDigits: 0 })} / month</p>
            <p style={{ fontSize: 12, color: 'var(--muted)', margin: '2px 0' }}>Total interest: Rs {totalInterest.toLocaleString(undefined, { maximumFractionDigits: 0 })}</p>
            <p style={{ fontSize: 12, color: 'var(--muted)', margin: '2px 0' }}>Total payment: Rs {totalPayment.toLocaleString(undefined, { maximumFractionDigits: 0 })}</p>
          </div>
          <p style={{ fontSize: 11, color: 'var(--muted)', marginTop: 8 }}>Estimate only - actual bank/mortgage terms may vary. This does not involve any real loan application.</p>
        </div>
      )}
    </div>
  );
}

export function AgentRatingWidget({ agent }) {
  const [ratings, setRatings] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (agent?.id) {
      realtyApi.agentRatings(agent.id).then((data) => setRatings(data.results || data)).catch(() => {});
    }
  }, [agent?.id]);

  const submit = async () => {
    setSubmitting(true);
    try {
      await realtyApi.rateAgent({ agent: agent.id, rating, comment });
      setShowForm(false);
      setComment('');
      const data = await realtyApi.agentRatings(agent.id);
      setRatings(data.results || data);
    } catch (e) {
      alert('Could not submit rating - you may have already rated this agent.');
    } finally {
      setSubmitting(false);
    }
  };

  if (!agent) return null;

  return (
    <div className="side-card">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <h3 style={{ fontSize: 16, margin: 0 }}>{t('agent_rating')}</h3>
        {agent.avg_rating > 0 && <span style={{ fontSize: 14, color: 'var(--emerald-dark)', fontWeight: 600 }}>⭐ {agent.avg_rating} ({agent.ratings_count})</span>}
      </div>

      {isRealtyLoggedIn() && !showForm && (
        <button className="btn btn-outline" style={{ fontSize: 13, marginBottom: 10 }} onClick={() => setShowForm(true)}>{t('rate_agent')}</button>
      )}

      {showForm && (
        <div style={{ marginBottom: 10 }}>
          <div style={{ display: 'flex', gap: 4, marginBottom: 8 }}>
            {[1, 2, 3, 4, 5].map((n) => (
              <span key={n} onClick={() => setRating(n)} style={{ fontSize: 22, cursor: 'pointer', opacity: n <= rating ? 1 : 0.3 }}>⭐</span>
            ))}
          </div>
          <textarea rows="2" placeholder={t('optional_comment')} value={comment} onChange={(e) => setComment(e.target.value)} style={{ width: '100%', padding: 8, fontSize: 13, border: '1px solid var(--border)', borderRadius: 8, marginBottom: 8, boxSizing: 'border-box' }} />
          <button className="btn btn-primary" style={{ fontSize: 13 }} disabled={submitting} onClick={submit}>{submitting ? 'Submitting...' : 'Submit rating'}</button>
        </div>
      )}

      {ratings.length === 0 ? <p className="muted" style={{ fontSize: 12 }}>{t('no_ratings_yet')}</p> : (
        ratings.slice(0, 5).map((r) => (
          <div key={r.id} style={{ borderTop: '1px solid var(--border)', paddingTop: 8, marginTop: 8 }}>
            <p style={{ fontSize: 13, margin: 0 }}>{'⭐'.repeat(r.rating)} <span className="muted">@{r.rater_username}</span></p>
            {r.comment && <p style={{ fontSize: 12, margin: '4px 0 0' }}>{r.comment}</p>}
          </div>
        ))
      )}
    </div>
  );
}
